import {
  AcrCategory,
  AcrProbability,
  AcrSeverity,
  AcrStatus,
  ClassificationLevel,
  EngagementType,
  Epoch,
  KillChainCategory,
  MopAggregation,
  ScoringRole,
  StudyAdversaryProfile,
  StudyCreateDtoClassificationCeilingEnum,
  StudyCreateDtoClassificationLevelEnum,
  StudyMissionCapabilityGapPriority,
  StudyMissionCapabilityGapStatus,
  StudyMoeCreateDtoAggregationMethodEnum,
  StudyMoeCreateDtoBlockingYellowBehaviorEnum,
  StudyMoeCreateDtoPendingMopBehaviorEnum,
  StudyMoeCreateDtoYellowDispositionEnum,
  StudyMopCreateDtoCategoryEnum,
  StudyMopCreateDtoDirectionEnum,
  StudyScoringAggregationMethod,
  StudyStakeholderRole,
  StudyTaskEffort,
  StudyThreadCreateDtoKillChainCategoryEnum,
  StudyThreadRole,
  StudyTimeHorizon,
  StudyUpdateDtoClassificationLevelEnum,
} from '@@types/api.types';
import { STUDY_MISSION_CONTEXT_MAX_LENGTHS } from '@azymmetric/shared/validation/meia.constants';
import { z } from 'zod';

export const meiaStudyCreateSchema = z.object({
  adversaryProfiles: z.array(z.nativeEnum(StudyAdversaryProfile)).optional(),
  classificationCeiling: z.nativeEnum(StudyCreateDtoClassificationCeilingEnum),
  classificationLevel: z.nativeEnum(StudyCreateDtoClassificationLevelEnum),
  description: z.string().max(2000, 'Description must be 2000 characters or fewer.').nullable().optional(),
  name: z.string().trim().min(2, 'Name must be at least 2 characters.').max(255, 'Name is too long.'),
  timeHorizon: z.nativeEnum(StudyTimeHorizon).nullable().optional(),
});

export const meiaStudyUpdateSchema = z.object({
  adversaryProfiles: z.array(z.nativeEnum(StudyAdversaryProfile)).optional(),
  classificationLevel: z.nativeEnum(StudyUpdateDtoClassificationLevelEnum),
  description: z.string().max(2000, 'Description must be 2000 characters or fewer.'),
  name: z.string().trim().min(2, 'Name must be at least 2 characters.').max(255, 'Name is too long.'),
  timeHorizon: z.nativeEnum(StudyTimeHorizon).nullable().optional(),
});

export const meiaStudyKopSchema = z.object({
  adversaryProfiles: z.array(z.nativeEnum(StudyAdversaryProfile)),
  keyOperationalProblem: z.string().max(4000, 'KOP must be 4000 characters or fewer.'),
  timeHorizon: z.nativeEnum(StudyTimeHorizon).nullable(),
});

export type MeiaStudyKopFormValues = z.infer<typeof meiaStudyKopSchema>;

export const meiaInvestigativeQuestionSchema = z.object({
  answer: z.string().max(4000, 'Answer must be 4000 characters or fewer.'),
  investigativeQuestionTemplateCode: z.string().nullable(),
  questionText: z.string().trim().min(1, 'Question is required.').max(2000, 'Question is too long.'),
});

export const meiaMissionSchema = z.object({
  capabilityGaps: z.array(
    z.object({
      id: z.string().uuid().optional(),
      position: z.number().min(0),
      priority: z.nativeEnum(StudyMissionCapabilityGapPriority),
      question: z.string().trim().min(1, 'Capability gap question is required.').max(2000, 'Question is too long.'),
      sourcePromptRef: z.string().trim().max(200).nullable().optional(),
      status: z.nativeEnum(StudyMissionCapabilityGapStatus),
    }),
  ),
  missionContext: z.object({
    areaOfOperations: z
      .string()
      .trim()
      .min(1, 'Area of Operations is required.')
      .max(STUDY_MISSION_CONTEXT_MAX_LENGTHS.areaOfOperations, 'AO is too long.'),
    assumptionsConstraints: z
      .string()
      .trim()
      .max(STUDY_MISSION_CONTEXT_MAX_LENGTHS.assumptionsConstraints, 'Assumptions & Constraints is too long.'),
    authoritiesRoeEnvelope: z
      .string()
      .trim()
      .max(STUDY_MISSION_CONTEXT_MAX_LENGTHS.authoritiesRoeEnvelope, 'Authorities / ROE Envelope is too long.'),
    blueForceComposition: z
      .string()
      .trim()
      .max(STUDY_MISSION_CONTEXT_MAX_LENGTHS.blueForceComposition, 'Blue Force Composition is too long.'),
    commandersIntent: z
      .string()
      .trim()
      .min(1, "Commander's Intent is required.")
      .max(STUDY_MISSION_CONTEXT_MAX_LENGTHS.commandersIntent, "Commander's Intent is too long."),
    desiredMissionOutcome: z
      .string()
      .trim()
      .min(1, 'Desired Mission Outcome is required.')
      .max(STUDY_MISSION_CONTEXT_MAX_LENGTHS.desiredMissionOutcome, 'Desired Mission Outcome is too long.'),
    timeWindow: z
      .string()
      .trim()
      .min(1, 'Time Window is required.')
      .max(STUDY_MISSION_CONTEXT_MAX_LENGTHS.timeWindow, 'Time Window is too long.'),
  }),
  missionTypeCategory: z.nativeEnum(KillChainCategory).nullable(),
  name: z.string().trim().min(1, 'Mission Name is required.').max(200, 'Mission Name is too long.'),
});

export const meiaStudyContextSchema = z.object({
  contextNarrative: z.string().trim().max(4000, 'Narrative is too long.'),
  environment: z.string().trim().max(4000, 'Environment is too long.'),
  threatEnvelope: z.string().trim().max(4000, 'Threat Envelope is too long.'),
});

export const meiaMoeSchema = z.object({
  aggregationMethod: z.nativeEnum(StudyMoeCreateDtoAggregationMethodEnum),
  blockingYellowBehavior: z.nativeEnum(StudyMoeCreateDtoBlockingYellowBehaviorEnum),
  description: z.string().trim().min(1, 'Description is required.').max(4000, 'Description is too long.'),
  moeTemplateCode: z.string().nullable(),
  pendingMopBehavior: z.nativeEnum(StudyMoeCreateDtoPendingMopBehaviorEnum),
  // `null` → leave empty so the backend will auto-distribute the weight.
  weight: z.number().min(0, 'Weight must be at least 0.').max(1, 'Weight cannot exceed 1.').nullable(),
  yellowDisposition: z.nativeEnum(StudyMoeCreateDtoYellowDispositionEnum),
});

export const meiaMopSchema = z.object({
  category: z.nativeEnum(StudyMopCreateDtoCategoryEnum),
  description: z.string().trim().min(1, 'Description is required.').max(4000, 'Description is too long.'),
  direction: z.nativeEnum(StudyMopCreateDtoDirectionEnum),
  mopTemplateCode: z.string().nullable(),
  name: z.string().trim().min(1, 'Name is required.').max(200, 'Name is too long.'),
  objective: z.string().trim().min(1, 'Objective is required.').max(500, 'Objective is too long.'),
  threshold: z.string().trim().min(1, 'Threshold is required.').max(500, 'Threshold is too long.'),
  unit: z.string().trim().min(1, 'Unit is required.').max(100, 'Unit is too long.'),
});

export const meiaAcrSchema = z.object({
  category: z.nativeEnum(AcrCategory),
  impact: z.nativeEnum(AcrSeverity).nullable(),
  likelihood: z.nativeEnum(AcrProbability).nullable(),
  mitigationApproach: z.string().trim().max(4000, 'Mitigation is too long.'),
  potentialImpact: z.string().trim().max(4000, 'Impact is too long.'),
  rationale: z.string().trim().min(1, 'Rationale is required.').max(4000, 'Rationale is too long.'),
  statement: z.string().trim().min(1, 'Statement is required.').max(4000, 'Statement is too long.'),
  status: z.nativeEnum(AcrStatus),
  title: z.string().trim().min(1, 'Title is required.').max(240, 'Title is too long.'),
});

export const meiaScoringConfigSchema = z
  .object({
    aggregationMethod: z.nativeEnum(StudyScoringAggregationMethod),
    greenThreshold: z
      .number()
      .min(0, 'Green threshold must be at least 0.')
      .max(100, 'Green threshold cannot exceed 100.'),
    yellowThreshold: z
      .number()
      .min(0, 'Yellow threshold must be at least 0.')
      .max(100, 'Yellow threshold cannot exceed 100.'),
  })
  .superRefine((data, ctx) => {
    if (data.yellowThreshold < data.greenThreshold) return;

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Yellow threshold must be lower than green threshold.',
      path: ['yellowThreshold'],
    });
  });

export const meiaRunEpochSchema = meiaScoringConfigSchema.and(
  z.object({
    epoch: z.nativeEnum(Epoch),
  }),
);

export const meiaMosSchema = z.object({
  description: z.string().trim().min(1, 'Description is required.').max(4000, 'Description is too long.'),
  mosTemplateCode: z.string().nullable(),
});

/** MoE entry within the nested MoS form. Mirrors meiaMoeSchema + a stable client id. */
export const meiaMoeEntrySchema = meiaMoeSchema.extend({
  _localId: z.string(),
  id: z.string().optional(),
});

const WEIGHT_SUM_TOLERANCE = 0.001;

/** Shared MoE weight-sum validation reused by every MoS form (single MoS modal + inline array). */
const refineMoeWeights = (data: { moeEntries: Array<{ weight: number | null }> }, ctx: z.RefinementCtx): void => {
  const explicit = data.moeEntries
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => typeof entry.weight === 'number');
  if (explicit.length === 0) return;

  const explicitSum = explicit.reduce((acc, { entry }) => acc + (entry.weight ?? 0), 0);
  const hasAuto = data.moeEntries.some((entry) => entry.weight === null || entry.weight === undefined);

  if (explicitSum > 1 + WEIGHT_SUM_TOLERANCE) {
    const message = `The weights you set add up to ${explicitSum.toFixed(2)}. Reduce some weights so the total is 1 or less.`;
    for (const { index } of explicit) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
        path: ['moeEntries', index, 'weight'],
      });
    }
    return;
  }

  if (!hasAuto && Math.abs(explicitSum - 1) > WEIGHT_SUM_TOLERANCE) {
    const message = `The weights add up to ${explicitSum.toFixed(2)}. They must total 1, or leave some blank for the backend to balance.`;
    for (const { index } of explicit) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
        path: ['moeEntries', index, 'weight'],
      });
    }
  }
};

/** Composite MoS form: MoS fields + nested MoE[] + tracked deletions for diff-on-submit. */
export const meiaMosWithChildrenSchema = meiaMosSchema
  .extend({
    moeEntries: z.array(meiaMoeEntrySchema),
    removedMoeIds: z.array(z.string()),
  })
  .superRefine(refineMoeWeights);

/** One MoS row inside the inline swimlane measures form: MoS fields + stable client id + nested MoE[]. */
export const meiaSwimlaneMosEntrySchema = meiaMosSchema
  .extend({
    _localId: z.string(),
    id: z.string().optional(),
    moeEntries: z.array(meiaMoeEntrySchema),
    removedMoeIds: z.array(z.string()),
  })
  .superRefine(refineMoeWeights);

/** Inline swimlane measures form: an array of MoS (each with nested MoE) + tracked MoS deletions. */
export const meiaSwimlaneMeasuresSchema = z.object({
  mosEntries: z.array(meiaSwimlaneMosEntrySchema),
  removedMosIds: z.array(z.string()),
});

export const meiaThreadSchema = z.object({
  killChainCategory: z.nativeEnum(StudyThreadCreateDtoKillChainCategoryEnum),
  name: z.string().trim().min(1, 'Thread name is required.').max(200, 'Name is too long.'),
  narrative: z.string().trim().min(1, 'Narrative is required.').max(4000, 'Narrative is too long.'),
  role: z.nativeEnum(StudyThreadRole),
});

export const meiaSwimlaneSchema = z.object({
  entryCriteria: z.string().max(4000, 'Entry criteria is too long.'),
  exitCriteria: z.string().max(4000, 'Exit criteria is too long.'),
  operationalPurpose: z.string().max(4000, 'Operational purpose is too long.'),
  phaseName: z.string().trim().min(1, 'Phase name is required.').max(200, 'Phase name is too long.'),
});

/**
 * MoS / MoE / MoP arrays are intentionally optional at the form level — a Task can be saved
 * as a draft without measure links, and the P3 readiness gate flags the missing pieces. Cross-stage
 * moves clear MoS/MoE links because they are scoped to the source stage.
 */
export const meiaTaskSchema = z.object({
  classification: z.nativeEnum(ClassificationLevel).nullable(),
  description: z.string().max(4000, 'Description is too long.').optional(),
  effort: z.nativeEnum(StudyTaskEffort).nullable(),
  expectedThreatTargetSystemCode: z.string().trim().max(200).nullable(),
  moeIds: z.array(z.string().uuid()),
  mopAggregation: z.nativeEnum(MopAggregation),
  mopIds: z.array(z.string().uuid()),
  mosIds: z.array(z.string().uuid()),
  owner: z.string().trim().max(200, 'Owner is too long.').nullable(),
  scoringRole: z.nativeEnum(ScoringRole),
  taskName: z.string().trim().min(1, 'Task name is required.').max(200, 'Task name is too long.'),
});

/** One Red Target Engagement row inside the task editor's dynamic engagements form. */
export const meiaTaskEngagementEntrySchema = z.object({
  _localId: z.string(),
  effectId: z.string().trim().min(1, 'Select the doctrinal effect.'),
  engagementType: z.nativeEnum(EngagementType).nullable(),
  id: z.string().optional(),
  pkEstimate: z
    .number({ invalid_type_error: 'pK must be a number between 0 and 1.' })
    .min(0, 'pK must be a number between 0 and 1.')
    .max(1, 'pK must be a number between 0 and 1.')
    .nullable(),
  taskTechnologyId: z.string().trim().min(1, 'Select the Blue technology.'),
  threatTargetComponentCode: z.string().nullable(),
  threatTargetSystemCode: z.string().trim().min(1, 'Select the Red target system.'),
});

/** Task engagements form: dynamic engagement rows + tracked deletions for diff-on-save. */
export const meiaTaskEngagementsSchema = z.object({
  engagements: z.array(meiaTaskEngagementEntrySchema),
  removedEngagements: z.array(z.object({ id: z.string(), taskTechnologyId: z.string() })),
});

export const meiaStakeholderSchema = z.object({
  organization: z.string().trim().max(200, 'Organization is too long.').nullable(),
  responsibility: z.string().trim().max(2000, 'Responsibility is too long.').nullable(),
  role: z.nativeEnum(StudyStakeholderRole),
  userId: z.string().uuid('User is required.'),
});

export type MeiaInvestigativeQuestionFormValues = z.infer<typeof meiaInvestigativeQuestionSchema>;
export type MeiaMissionFormValues = z.infer<typeof meiaMissionSchema>;
export type MeiaMoeFormValues = z.infer<typeof meiaMoeSchema>;
export type MeiaMoeEntryFormValues = z.infer<typeof meiaMoeEntrySchema>;
export type MeiaAcrFormValues = z.infer<typeof meiaAcrSchema>;
export type MeiaMopFormValues = z.infer<typeof meiaMopSchema>;
export type MeiaMosFormValues = z.infer<typeof meiaMosSchema>;
export type MeiaMosWithChildrenFormValues = z.infer<typeof meiaMosWithChildrenSchema>;
export type MeiaSwimlaneMosEntryFormValues = z.infer<typeof meiaSwimlaneMosEntrySchema>;
export type MeiaSwimlaneMeasuresFormValues = z.infer<typeof meiaSwimlaneMeasuresSchema>;
export type MeiaScoringConfigFormValues = z.infer<typeof meiaScoringConfigSchema>;
export type MeiaRunEpochFormValues = z.infer<typeof meiaRunEpochSchema>;
export type MeiaStudyContextFormValues = z.infer<typeof meiaStudyContextSchema>;
export type MeiaStudyUpdateFormValues = z.infer<typeof meiaStudyUpdateSchema>;
export type MeiaStakeholderFormValues = z.infer<typeof meiaStakeholderSchema>;
export type MeiaTaskEngagementEntryFormValues = z.infer<typeof meiaTaskEngagementEntrySchema>;
export type MeiaTaskEngagementsFormValues = z.infer<typeof meiaTaskEngagementsSchema>;
export type MeiaSwimlaneFormValues = z.infer<typeof meiaSwimlaneSchema>;
export type MeiaTaskFormValues = z.infer<typeof meiaTaskSchema>;
export type MeiaThreadFormValues = z.infer<typeof meiaThreadSchema>;
